import { useState } from 'react';
import { Grid, Typography, Box, Divider, Alert, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MetricCard from './MetricCard';
import MetricExplanationModal from './MetricExplanationModal';
import { smoothSlideIn, refinedEasing } from '../theme/animations';

/**
 * ExecutiveDashboard - CFO-Level Financial Metrics Display
 *
 * Presents all 6 tiers of financial metrics in a scannable 3-column grid
 * with conditional rendering for company type (public/private/REIT).
 */
export default function ExecutiveDashboard({ cfoMetrics, companyConfig }) {
  const [openMetric, setOpenMetric] = useState(null);

  // Return null only if completely missing - component should ALWAYS render when data exists
  if (!cfoMetrics) {
    return null;
  }

  // Use optional chaining and fallbacks for all tiers
  const tier1_ebitda = cfoMetrics?.tier1_ebitda || null;
  const tier2_revenue = cfoMetrics?.tier2_revenue || null;
  const tier3_valuation = cfoMetrics?.tier3_valuation || null;
  const tier4_capital = cfoMetrics?.tier4_capital || cfoMetrics?.capital_efficiency || null;
  const tier5_esg = cfoMetrics?.tier5_esg || cfoMetrics?.esg || null;
  const tier6_reit = cfoMetrics?.tier6_reit || null;
  const tier7_public = cfoMetrics?.tier7_public || null;
  const configuration = cfoMetrics?.configuration || {};

  // Check if we have ANY data to show - render header even if some tiers missing
  const hasAnyData = tier1_ebitda || tier2_revenue || tier3_valuation || tier4_capital || tier5_esg;

  if (!hasAnyData) {
    // Still render header with loading/pending state instead of error
    return (
      <Box sx={{ my: 6 }}>
        <Typography
          sx={{
            fontSize: '2.5rem',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            mb: 1,
          }}
        >
          Financial Impact Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Calculating financial metrics...
        </Typography>
      </Box>
    );
  }

  const isREIT = configuration.companyType === 'reit';
  const isPublic = configuration.companyType === 'public';

  // Utility formatters
  const formatCurrency = (value) => {
    if (value == null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value == null) return 'N/A';
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatNumber = (value, decimals = 0) => {
    if (value == null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  return (
    <Box sx={{ my: 6 }}>
      {/* Section Header - BOLD & REFINED */}
      <Box
        sx={{
          mb: 5,
        }}
      >
        <Typography
          sx={{
            fontSize: '2.5rem',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            mb: 1,
            position: 'relative',
            pb: 2,
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: 60,
              height: 3,
              background: (theme) =>
                `linear-gradient(90deg, ${theme.palette.primary.main}, transparent)`,
              boxShadow: (theme) => `0 0 10px ${theme.palette.primary.main}66`,
            },
          }}
        >
          Financial Impact Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
          Executive-level financial metrics translating operational savings into strategic value
        </Typography>
      </Box>

      {/* Row 1: Core Financial Metrics */}
      {(tier1_ebitda || tier2_revenue || tier3_valuation) && (
        <>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              fontSize: '0.75rem',
            }}
          >
            Core Financial Metrics
          </Typography>
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {/* EBITDA Impact */}
            {tier1_ebitda && (
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                  animationDelay: '0.1s',
                  animationFillMode: 'backwards',
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <MetricCard
                    title="EBITDA Impact"
                    value={`${formatCurrency(tier1_ebitda.contribution)}/yr`}
                    subtitle={
                      tier1_ebitda.marginImprovement
                        ? `${tier1_ebitda.marginImprovement.toFixed(3)}% margin impact`
                        : 'Based on corpus-validated savings'
                    }
                    color="success"
                  />
                  <IconButton
                    size="small"
                    onClick={() => setOpenMetric('ebitda_impact')}
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      p: 0.5,
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Grid>
            )}

            {/* Revenue Equivalence */}
            {tier2_revenue && (
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                  animationDelay: '0.2s',
                  animationFillMode: 'backwards',
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <MetricCard
                    title="Revenue Equivalent"
                    value={formatCurrency(tier2_revenue.equivalentRevenue)}
                    subtitle={
                      tier2_revenue.revenueGrowthPercent
                        ? `${formatNumber(tier2_revenue.revenueGrowthPercent, 1)}% growth required for same impact`
                        : `At ${((tier2_revenue.profitMarginUsed || 0.05) * 100).toFixed(1)}% profit margin`
                    }
                    color="primary"
                  />
                  <IconButton
                    size="small"
                    onClick={() => setOpenMetric('revenue_equivalent')}
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      p: 0.5,
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Grid>
            )}

            {/* Market Cap / Enterprise Value */}
            {tier3_valuation && (
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                  animationDelay: '0.3s',
                  animationFillMode: 'backwards',
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <MetricCard
                    title={isPublic ? 'Market Cap Impact' : 'Enterprise Value'}
                    value={formatCurrency(
                      tier3_valuation.marketCapIncrease || tier3_valuation.enterpriseValueIncrease || tier3_valuation.valueIncrease
                    )}
                    subtitle={`@ ${tier3_valuation.multipleUsed || 8}x EBITDA multiple`}
                    color="info"
                  />
                  <IconButton
                    size="small"
                    onClick={() => setOpenMetric('market_cap_impact')}
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      p: 0.5,
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Grid>
            )}
          </Grid>
        </>
      )}

      {/* Row 2: Capital Efficiency */}
      {tier4_capital && (
        <>
          <Divider sx={{ my: 4 }} />
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              fontSize: '0.75rem',
            }}
          >
            Capital Efficiency
          </Typography>
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {/* Payback & NPV */}
            {(tier4_capital.paybackMonths != null || tier4_capital.npv != null) && (
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                  animationDelay: '0.4s',
                  animationFillMode: 'backwards',
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <MetricCard
                    title="Payback & NPV"
                    value={`${formatNumber(tier4_capital.paybackMonths, 1)} mo`}
                    subtitle={`NPV: ${formatCurrency(tier4_capital.npv)} @ ${((tier4_capital.waccUsed || 0.08) * 100).toFixed(0)}% WACC`}
                    color="primary"
                  />
                  <IconButton
                    size="small"
                    onClick={() => setOpenMetric('npv')}
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      p: 0.5,
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Grid>
            )}

            {/* Internal Rate of Return */}
            {tier4_capital.irr != null && (
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                  animationDelay: '0.5s',
                  animationFillMode: 'backwards',
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <MetricCard
                    title="Internal Rate of Return"
                    value={`${formatNumber(tier4_capital.irr * 100, 0)}%`}
                    subtitle={`vs. ${((tier4_capital.waccUsed || 0.08) * 100).toFixed(0)}% cost of capital`}
                    color="success"
                  />
                  <IconButton
                    size="small"
                    onClick={() => setOpenMetric('irr')}
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      p: 0.5,
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Grid>
            )}

            {/* Return on Investment */}
            {tier4_capital.roic != null && (
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                  animationDelay: '0.6s',
                  animationFillMode: 'backwards',
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <MetricCard
                    title="Return on Investment"
                    value={`${formatNumber(tier4_capital.roic * 100, 0)}%`}
                    subtitle="Annual return on invested capital"
                    color="success"
                  />
                  <IconButton
                    size="small"
                    onClick={() => setOpenMetric('roic')}
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      p: 0.5,
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Grid>
            )}
          </Grid>
        </>
      )}

      <Divider sx={{ my: 4 }} />

      {/* Row 3: REIT, Public Company, or ESG-specific */}
      {isREIT && tier6_reit ? (
        <>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              fontSize: '0.75rem',
            }}
          >
            REIT Financial Metrics
          </Typography>
          <Grid container spacing={4}>
            {/* NOI Improvement */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                animationDelay: '0.7s',
                animationFillMode: 'backwards',
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <MetricCard
                  title="NOI Improvement"
                  value={`${formatCurrency(tier6_reit.noiImprovement)}/yr`}
                  subtitle="Direct net operating income increase"
                  color="primary"
                />
                <IconButton
                  size="small"
                  onClick={() => setOpenMetric('noi_improvement')}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    p: 0.5,
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Grid>

            {/* Property Value Increase */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                animationDelay: '0.8s',
                animationFillMode: 'backwards',
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <MetricCard
                  title="Property Value Increase"
                  value={formatCurrency(tier6_reit.propertyValueIncrease)}
                  subtitle={`@ ${(tier6_reit.currentCapRate * 100).toFixed(1)}% cap rate`}
                  color="success"
                />
                <IconButton
                  size="small"
                  onClick={() => setOpenMetric('property_value_increase')}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    p: 0.5,
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Grid>

            {/* FFO/AFFO Impact */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                animationDelay: '0.9s',
                animationFillMode: 'backwards',
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <MetricCard
                  title="FFO/AFFO Impact"
                  value={`${formatCurrency(tier6_reit.ffoImpact)}/yr`}
                  subtitle="Funds from operations increase"
                  color="info"
                />
                <IconButton
                  size="small"
                  onClick={() => setOpenMetric('ffo_impact')}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    p: 0.5,
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Grid>

            {/* NOI per SF Improvement */}
            {tier6_reit.noiPerSFImprovement !== null && (
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                  animationDelay: '0.8s',
                  animationFillMode: 'backwards',
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <MetricCard
                    title="NOI per SF Improvement"
                    value={`+$${tier6_reit.noiPerSFImprovement.toFixed(2)}/SF`}
                    subtitle={`On ${formatNumber(tier6_reit.squareFootage, 0)} SF property`}
                    color="primary"
                  />
                </Box>
              </Grid>
            )}

            {/* OpEx Ratio Reduction */}
            {tier6_reit.opExRatioReduction !== null && (
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                  animationDelay: '0.85s',
                  animationFillMode: 'backwards',
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <MetricCard
                    title="OpEx Ratio Reduction"
                    value={`-${tier6_reit.opExRatioReduction.toFixed(2)}%`}
                    subtitle="Operating expense reduction"
                    color="success"
                  />
                </Box>
              </Grid>
            )}

            {/* DSCR Impact */}
            {tier6_reit.dscrImpact !== null && (
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                  animationDelay: '0.9s',
                  animationFillMode: 'backwards',
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <MetricCard
                    title="DSCR Impact"
                    value={`+${tier6_reit.dscrImpact.toFixed(3)}x`}
                    subtitle="Debt service coverage boost"
                    color="warning"
                  />
                </Box>
              </Grid>
            )}

            {/* Cap Rate Yield Improvement */}
            {tier6_reit.capRateYieldImprovement !== null && (
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                  animationDelay: '0.95s',
                  animationFillMode: 'backwards',
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <MetricCard
                    title="Cap Rate Yield Boost"
                    value={`+${(tier6_reit.capRateYieldImprovement * 100).toFixed(0)} bps`}
                    subtitle="Effective yield improvement"
                    color="info"
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </>
      ) : isPublic && tier7_public ? (
        <>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              fontSize: '0.75rem',
            }}
          >
            Public Company Metrics
          </Typography>
          <Grid container spacing={4}>
            {/* EPS Impact */}
            {tier7_public.epsImpact !== null && (
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                  animationDelay: '0.7s',
                  animationFillMode: 'backwards',
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <MetricCard
                    title="EPS Impact"
                    value={`+$${tier7_public.epsImpact.toFixed(4)}/share`}
                    subtitle={`On ${formatNumber(tier7_public.sharesOutstanding, 0)} shares`}
                    color="success"
                  />
                </Box>
              </Grid>
            )}

            {/* Operating Margin Improvement */}
            {tier7_public.opMarginImprovement !== null && (
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                  animationDelay: '0.75s',
                  animationFillMode: 'backwards',
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <MetricCard
                    title="Operating Margin Improvement"
                    value={`+${tier7_public.opMarginImprovement.toFixed(2)}%`}
                    subtitle="OpEx ratio reduction"
                    color="success"
                  />
                </Box>
              </Grid>
            )}

            {/* Free Cash Flow Impact */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                animationDelay: '0.8s',
                animationFillMode: 'backwards',
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <MetricCard
                  title="Free Cash Flow Impact"
                  value={`${formatCurrency(tier7_public.fcfImpact)}/yr`}
                  subtitle="Direct FCF improvement"
                  color="primary"
                />
              </Box>
            </Grid>

            {/* Scope 2 Emissions Reduction */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                animationDelay: '0.85s',
                animationFillMode: 'backwards',
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <MetricCard
                  title="Scope 2 Emissions Reduction"
                  value={`${tier7_public.scope2Reduction.toFixed(1)} tonnes CO₂`}
                  subtitle="SEC climate disclosure metric"
                  color="info"
                />
              </Box>
            </Grid>

            {/* Carbon Cost Avoidance */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                animationDelay: '0.9s',
                animationFillMode: 'backwards',
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <MetricCard
                  title="Carbon Cost Avoidance"
                  value={`${formatCurrency(tier7_public.carbonCostAvoidance)}/yr`}
                  subtitle={`@ $${tier7_public.carbonPrice}/tonne carbon price`}
                  color="warning"
                />
              </Box>
            </Grid>

            {/* P/E Value Impact */}
            {tier7_public.peValueImpact !== null && (
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                  animationDelay: '0.95s',
                  animationFillMode: 'backwards',
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <MetricCard
                    title="P/E Value Impact"
                    value={formatCurrency(tier7_public.peValueImpact)}
                    subtitle={`@ ${tier7_public.peRatio}x P/E multiple`}
                    color="success"
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </>
      ) : tier5_esg ? (
        <>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              fontSize: '0.75rem',
            }}
          >
            ESG & Sustainability Impact
          </Typography>
          <Grid container spacing={4}>
            {/* Carbon Reduction */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                animationDelay: '0.7s',
                animationFillMode: 'backwards',
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <MetricCard
                  title="Carbon Reduction"
                  value={`${formatNumber(tier5_esg.co2AvoidedTonnes || tier5_esg.carbonReduction || 0, 1)} tonnes CO₂/yr`}
                  subtitle={`Equivalent to removing ${tier5_esg.vehicleEquivalent || tier5_esg.vehiclesRemoved || 0} cars from the road`}
                  color="success"
                />
                <IconButton
                  size="small"
                  onClick={() => setOpenMetric('co2_avoided')}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    p: 0.5,
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Grid>

            {/* ESG Impact */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                animation: `${smoothSlideIn} 0.6s ${refinedEasing}`,
                animationDelay: '0.8s',
                animationFillMode: 'backwards',
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <MetricCard
                  title="ESG Reporting Value"
                  value={`${formatNumber(tier5_esg.treesEquivalent || 0)} trees`}
                  subtitle="ISSB/TCFD compliant carbon reduction measurement"
                  color="info"
                />
                <IconButton
                  size="small"
                  onClick={() => setOpenMetric('vehicle_equivalent')}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    p: 0.5,
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </>
      ) : null}

      {/* Metric Explanation Modal */}
      <MetricExplanationModal
        metric={openMetric}
        open={Boolean(openMetric)}
        onClose={() => setOpenMetric(null)}
      />
    </Box>
  );
}
